/**
 * Uploads: photos are resized and recompressed in the browser before upload
 * (NFR-02: slow mobile data, low-end phones). Private files are fetched with the
 * token and shown as blob URLs; public ones load straight from /api/v1/files/public.
 */
import { useEffect, useState } from 'react';
import { ApiError, getAccessToken } from './api';

export interface Uploaded { id: string; mimeType: string; size: number; filename: string }

/** Longest side 1600 px, JPEG quality 0.78 — a phone photo goes from ~4 MB to ~250 KB. */
export async function compressImage(file: File, maxSide = 1600, quality = 0.78): Promise<Blob> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', quality));
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

export async function upload(file: File | Blob, kind: string, name = 'photo.jpg', onProgress?: (pct: number) => void): Promise<Uploaded> {
  const body = file instanceof File && file.type.startsWith('image/') ? await compressImage(file) : file;
  const form = new FormData();
  form.append('file', body, file instanceof File ? file.name.replace(/\.(heic|heif|png|webp)$/i, '.jpg') : name);
  const send = () => new Promise<{ status: number; text: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/v1/files?kind=${encodeURIComponent(kind)}`);
    const token = getAccessToken();
    if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
    xhr.onerror = () => reject(new ApiError(0, 'Upload failed. Check your connection and try again.'));
    xhr.send(form);
  });
  let res = await send();
  if (res.status === 401) {
    const r = await fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'include' });
    if (r.ok) {
      const { setAccessToken } = await import('./api');
      setAccessToken(((await r.json()) as { accessToken: string }).accessToken);
      res = await send();
    }
  }
  const payload = res.text ? (JSON.parse(res.text) as Record<string, unknown>) : {};
  if (res.status >= 400) throw new ApiError(res.status, String(payload.message ?? 'Upload failed.'));
  return payload as unknown as Uploaded;
}

export const publicFileUrl = (id: string | null | undefined): string | undefined => (id ? `/api/v1/files/public/${id}` : undefined);

const blobCache = new Map<string, string>();

/** A private file as an object URL (cached for the session). */
export function usePrivateFile(id: string | null | undefined): string | undefined {
  const [url, setUrl] = useState<string | undefined>(id ? blobCache.get(id) : undefined);
  useEffect(() => {
    if (!id || blobCache.has(id)) { setUrl(id ? blobCache.get(id) : undefined); return; }
    let live = true;
    void fetch(`/api/v1/files/${id}`, { headers: { authorization: `Bearer ${getAccessToken() ?? ''}` }, credentials: 'include' })
      .then(async (r) => (r.ok ? URL.createObjectURL(await r.blob()) : undefined))
      .then((u) => { if (u) blobCache.set(id, u); if (live) setUrl(u); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [id]);
  return url;
}

/** Opens a private file (agreement PDF, receipt) in a new tab. */
export async function openFile(id: string): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const r = await fetch(`/api/v1/files/${id}`, { headers: { authorization: `Bearer ${getAccessToken() ?? ''}` }, credentials: 'include' });
    if (!r.ok) throw new Error('Could not open the file.');
    const url = URL.createObjectURL(await r.blob());
    if (tab) tab.location.href = url; else window.location.href = url;
  } catch (e) {
    tab?.close();
    throw e;
  }
}
